// src/pages/Report.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavigationContext } from '../context/NavigationContext';
import { ChatContext } from '../context/ChatContext';
import { useWarnings } from '../context/WarningsContext';
import Plot from 'react-plotly.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearchPlus, 
  faExclamationTriangle, 
  faQuestionCircle,
  faTimesCircle,
  faPencil,
  faFilePdf,
  faFileCsv,
  faSave,
  faToggleOn,
  faToggleOff
} from '@fortawesome/free-solid-svg-icons';
import Banner from '../components/Banner';
import DemoWalkthrough, { DontShowLink } from '../components/DemoWalkthrough';
import { setStep, isDismissed } from '../utils/walkthroughState';
import { ThemeContext } from '../context/ThemeContext';
import useWindowResize from '../hooks/useWindowResize';
import API_URL from '../config';

const Report = () => {
  const navigate = useNavigate();
  const { recordingId } = useParams();
  const { openLevel } = useContext(NavigationContext);
  const { setChatDictionary } = useContext(ChatContext);
  const { isDarkMode } = useContext(ThemeContext);

  const { showWarnings } = useWarnings();
  const windowSize = useWindowResize();

  const [tourActive, setTourActive] = useState(false);
  
  // State for patient information
  const [patientInfo, setPatientInfo] = useState(null);
  const [recordingInfo, setRecordingInfo] = useState(null);
  const [sleepParameters, setSleepParameters] = useState({});
  const [epochs, setEpochs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [editError, setEditError] = useState(null);
  const [updatingPatient, setUpdatingPatient] = useState(false);

  // Fetch all required data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch recording information first
        const recordingResponse = await fetch(`${API_URL}/api/recording/${recordingId}/`);
        const recordingData = await recordingResponse.json();
        setRecordingInfo(recordingData);

        // Fetch patient information using the recording's patient ID
        const patientResponse = await fetch(`${API_URL}/api/patient/${recordingData.patient}/`);
        const patientData = await patientResponse.json();
        setPatientInfo(patientData);

        // Fetch sleep parameters
        const parametersResponse = await fetch(`${API_URL}/api/sleep-parameters/?recording_id=${recordingId}`);
        const parametersData = await parametersResponse.json();
        const parametersMap = {};
        parametersData.forEach(param => {
          parametersMap[param.name] = {
            value: Number(param.value).toFixed(1),
            disagreementWarning: param.disagreement_warning,
            disagreementWarningText: param.disagreement_warning_text,
            uncertaintyWarning: param.uncertainty_warning,
            uncertaintyWarningText: param.uncertainty_warning_text
          };
        });
        setSleepParameters(parametersMap);

        // Fetch epochs data
        const epochsResponse = await fetch(`${API_URL}/api/epochs/?recording_id=${recordingId}`);
        const epochsData = await epochsResponse.json();
        setEpochs(epochsData);

        // Fetch summary using the new endpoint
        const summaryResponse = await fetch(`${API_URL}/api/recording/${recordingId}/summary/`);
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          setSummary(summaryData);
        } else {
          throw new Error('Failed to fetch summary');
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update chat context when data changes
  useEffect(() => {
    if (patientInfo && recordingInfo && sleepParameters && epochs) {
      // Calculate sleep stages distribution
      const stageCounts = {};
      epochs.forEach(epoch => {
        const stage = epoch.sleep_stage || 'undefined';
        stageCounts[stage] = (stageCounts[stage] || 0) + 1;
      });

      // Convert to percentages
      const totalEpochs = epochs.length;
      const stagePercentages = {};
      Object.entries(stageCounts).forEach(([stage, count]) => {
        stagePercentages[stage] = (count / totalEpochs) * 100;
      });

      const chatDictionary = {
        level: 'report',
        patientInfo: {
          name: patientInfo.name,
          age: patientInfo.age,
          gender: patientInfo.gender,
          patient_id: patientInfo.patient_id
        },
        recordingInfo: {
          recording_date: recordingInfo.recording_date,
          signal_quality: recordingInfo.signal_quality,
          total_recording_time: recordingInfo.total_recording_time,
          lights_out_time: recordingInfo.lights_out_time,
          lights_on_time: recordingInfo.lights_on_time
        },
        sleepParameters: sleepParameters,
        stagePercentages: stagePercentages
      };
      setChatDictionary(chatDictionary);
    }
  }, [patientInfo, recordingInfo, sleepParameters, epochs, setChatDictionary]);

  const handleOpenParameters = (paramName) => {
    // Only prime the walkthrough resume token if the tour is currently active.
    if (tourActive && recordingInfo?.is_demo && paramName === 'N3 Duration') {
      setStep('parameter-detail', recordingId);
    }
    openLevel({ 
      key: 'parameterDetail', 
      label: paramName, 
      route: `/analysis/${recordingId}/parameter-detail?param=${encodeURIComponent(paramName)}` 
    });
  };

  // Sleep stages pie chart data
  const sleepStagesData = [
    {
      values: [
        Number(sleepParameters['Wake Duration']?.value) || 0,
        Number(sleepParameters['N1 Duration']?.value) || 0,
        Number(sleepParameters['N2 Duration']?.value) || 0,
        Number(sleepParameters['N3 Duration']?.value) || 0,
        Number(sleepParameters['REM Duration']?.value) || 0,
      ],
      labels: ['Wake', 'N1', 'N2', 'N3', 'REM'],
      type: 'pie',
      hole: 0.4,
      textinfo: 'percent',
      textposition: 'inside',
      marker: {
        colors: isDarkMode ? [
          '#64748B', // slate-500 for Wake
          '#94A3B8', // slate-400 for N1
          '#CBD5E1', // slate-300 for N2
          '#E2E8F0', // slate-200 for N3
          '#475569'  // slate-600 for REM
        ] : [
          '#94A3B8', // slate-400 for Wake
          '#CBD5E1', // slate-300 for N1
          '#E2E8F0', // slate-200 for N2
          '#F1F5F9', // slate-100 for N3
          '#CBD5E1'  // slate-300 for REM
        ]
      }
    }
  ];

  const sleepStagesLayout = {
    title: 'Sleep Stages Distribution',
    showlegend: true,
    height: 300,
    margin: { t: 0, b: 0, l: 0, r: 0, pad: 0 },
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: -0.2,
      xanchor: 'center',
      x: 0.5,
      font: {
        color: isDarkMode ? '#E2E8F0' : '#64748B' // slate-200 in dark mode, slate-500 in light mode
      }
    },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: isDarkMode ? '#E2E8F0' : '#64748B' // slate-200 in dark mode, slate-500 in light mode
    }
  };

  // Hypnogram data
  const stageMapping = { 'Wake': 4, 'REM': 3, 'N1': 2, 'N2': 1, 'N3': 0 };
  
  // Create arrays for the line segments
  let x = [];
  let y = [];
  
  epochs.forEach((epoch, i) => {
    // Add current point using timestamp
    x.push(new Date(epoch.epoch_start));
    y.push(stageMapping[epoch.sleep_stage] ?? null);
    
    // If not the last point, add a vertical line to the next stage
    if (i < epochs.length - 1) {
      const currentStage = stageMapping[epoch.sleep_stage] ?? null;
      const nextStage = stageMapping[epochs[i + 1].sleep_stage] ?? null;
      if (currentStage !== nextStage && currentStage !== null && nextStage !== null) {
        // Add points for vertical line at the same timestamp
        x.push(new Date(epoch.epoch_start));
        y.push(nextStage);
      }
    }
  });

  // Create shapes for error warnings first
  const errorShapes = [];
  let i = 0;
  while (i < epochs.length) {
    const epoch = epochs[i];
    if (epoch.error_warning) {
      // Find the end of this continuous error region
      let endIndex = i;
      while (endIndex + 1 < epochs.length && epochs[endIndex + 1].error_warning) {
        endIndex++;
      }
      
      // Add a single shape for this continuous region
      errorShapes.push({
        type: 'rect',
        x0: new Date(epochs[i].epoch_start),
        x1: new Date(epochs[endIndex].epoch_end),
        y0: -1,
        y1: 4.5,
        fillcolor: isDarkMode ? 'rgb(255, 85, 85)' : 'rgb(220, 38, 38)', // Vibrant red in dark mode, red-600 in light mode
        opacity: 0.4,
        line: { width: 0 },
        layer: 'below'
      });
      
      // Skip to after this region
      i = endIndex + 1;
    } else {
      i++;
    }
  }

  // Create shapes for disagreement warnings, but only where there's no error
  const disagreementShapes = [];
  i = 0;
  while (i < epochs.length) {
    const epoch = epochs[i];
    if (epoch.disagreement_warning && !epoch.error_warning) {
      // Find the end of this continuous disagreement region
      let endIndex = i;
      while (endIndex + 1 < epochs.length && 
             epochs[endIndex + 1].disagreement_warning && 
             !epochs[endIndex + 1].error_warning) {
        endIndex++;
      }
      
      // Add a single shape for this continuous region
      disagreementShapes.push({
        type: 'rect',
        x0: new Date(epochs[i].epoch_start),
        x1: new Date(epochs[endIndex].epoch_end),
        y0: -1,
        y1: 4.5,
        fillcolor: isDarkMode ? 'rgb(255, 215, 0)' : 'rgb(234, 179, 8)', // Gold yellow in dark mode, yellow-500 in light mode
        opacity: 0.3,
        line: { width: 0 },
        layer: 'below'
      });
      
      // Skip to after this region
      i = endIndex + 1;
    } else {
      i++;
    }
  }

  // Create shapes for uncertainty warnings, but only where there's no error or disagreement
  const uncertaintyShapes = [];
  i = 0;
  while (i < epochs.length) {
    const epoch = epochs[i];
    if (epoch.uncertainty_warning && !epoch.error_warning && !epoch.disagreement_warning) {
      // Find the end of this continuous uncertainty region
      let endIndex = i;
      while (endIndex + 1 < epochs.length && 
             epochs[endIndex + 1].uncertainty_warning && 
             !epochs[endIndex + 1].error_warning &&
             !epochs[endIndex + 1].disagreement_warning) {
        endIndex++;
      }
      
      // Add a single shape for this continuous region
      uncertaintyShapes.push({
        type: 'rect',
        x0: new Date(epochs[i].epoch_start),
        x1: new Date(epochs[endIndex].epoch_end),
        y0: -1,
        y1: 4.5,
        fillcolor: isDarkMode ? 'rgb(147, 51, 234)' : 'rgb(168, 85, 247)', // Bright purple in dark mode, purple-500 in light mode
        opacity: 0.3,
        line: { width: 0 },
        layer: 'below'
      });
      
      // Skip to after this region
      i = endIndex + 1;
    } else {
      i++;
    }
  }

  // Create separate segments for continuous REM periods
  let remX = [];
  let remY = [];
  
  epochs.forEach((epoch, i) => {
    if (epoch.sleep_stage === 'REM') {
      // Add this REM point using timestamp
      remX.push(new Date(epoch.epoch_start));
      remY.push(stageMapping['REM']);
      
      // If this is the last epoch or next epoch is not REM, end this segment
      if (i === epochs.length - 1 || epochs[i + 1].sleep_stage !== 'REM') {
        remX.push(null);
        remY.push(null);
      }
    }
  });

  const hypnogramData = [
    {
      x: x,
      y: y,
      type: 'scatter',
      mode: 'lines',
      line: {
        color: isDarkMode ? '#FFFFFF' : '#000000',
        width: 1
      }
    },
    // Separate trace for REM stages with red color
    {
      x: remX,
      y: remY,
      type: 'scatter',
      mode: 'lines',
      line: {
        color: isDarkMode ? '#FF8080' : '#DC2626', // Light red in dark mode, darker red in light mode
        width: 3
      }
    }
  ];

  const hypnogramLayout = {
    title: 'Hypnogram',
    showlegend: false,
    xaxis: { 
      title: 'Time',
      visible: true,
      showline: false,
      showgrid: false,
      gridcolor: '#374151',
      zerolinecolor: '#4B5563',
      type: 'date',
      tickformat: '%H:%M'
    },
    yaxis: {
      title: 'Sleep Stage',
      tickvals: [0, 1, 2, 3, 4],
      ticktext: ['N3', 'N2', 'N1', 'REM', 'Wake'],
      range: [-1, 4.5],
      fixedrange: true,
      showgrid: false,
      zeroline: false,
      gridcolor: '#374151',
      zerolinecolor: '#4B5563'
    },
    margin: {
      l: 50,
      r: 20,
      t: 30,
      b: 50
    },
    shapes: [
      // Base line
      {
        type: 'line',
        x0: epochs.length > 0 ? new Date(epochs[0].epoch_start) : new Date(),
        x1: epochs.length > 0 ? new Date(epochs[epochs.length - 1].epoch_end) : new Date(),
        y0: -0.5,
        y1: -0.5,
        line: {
          color: '#4B5563',
          width: 1
        }
      },
      // Add warning shapes in order of precedence (lowest to highest)
      ...(showWarnings ? [
        ...uncertaintyShapes,
        ...disagreementShapes,
        ...errorShapes
      ] : [])
    ],
    height: 300,
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: {
      color: '#9CA3AF'
    }
  };

  const handleEditSummary = () => {
    const textToEdit = summary?.summary_text || '';
    setEditedSummary(textToEdit);
    setIsEditing(true);
  };

  const handleSaveSummary = async () => {
    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await fetch(`${API_URL}/api/recording/${recordingId}/summary/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          summary_text: editedSummary,
          is_automatically_generated: false
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save summary. Please try again.');
      }

      setSummary(data);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving summary:', err);
      setSaveError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSummary('');
  };

  const generatePDF = async () => {
    setGeneratingPdf(true);
    try {
      const response = await fetch(`${API_URL}/api/generate-pdf/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientInfo,
          recordingInfo,
          sleepParameters,
          summary,
          // Convert plot data to base64 images on the backend
          plotData: {
            sleepStages: sleepStagesData,
            hypnogram: hypnogramData
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }

      // Get the PDF blob from the response
      const blob = await response.blob();
      
      // Create a download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sleep_report_${patientInfo.patient_id}_${recordingInfo.recording_date}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      // You might want to show an error message to the user here
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSaveEdit = async (field) => {
    setUpdatingPatient(true);
    try {
      const response = await fetch(`${API_URL}/api/patient/${patientInfo.id}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          [field]: editValue
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update patient information');
      }

      const updatedPatient = await response.json();
      setPatientInfo(updatedPatient);
      setEditingField(null);
      setEditValue('');
      setEditError(null);

      // Refresh epochs and sleep parameters after patient update
      const epochsResponse = await fetch(`${API_URL}/api/epochs/?recording_id=${recordingId}`);
      const epochsData = await epochsResponse.json();
      setEpochs(epochsData);

      const parametersResponse = await fetch(`${API_URL}/api/sleep-parameters/?recording_id=${recordingId}`);
      const parametersData = await parametersResponse.json();
      const parametersMap = {};
      parametersData.forEach(param => {
        parametersMap[param.name] = {
          value: Number(param.value).toFixed(1),
          disagreementWarning: param.disagreement_warning,
          disagreementWarningText: param.disagreement_warning_text,
          uncertaintyWarning: param.uncertainty_warning,
          uncertaintyWarningText: param.uncertainty_warning_text
        };
      });
      setSleepParameters(parametersMap);
    } catch (err) {
      console.error('Error updating patient:', err);
      setEditError(err.message || 'An error occurred while updating');
    } finally {
      setUpdatingPatient(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600 dark:text-gray-300">Loading report data...</div>;
  }

  // Show loading modal while updating patient
  if (updatingPatient) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 flex flex-col items-center">
          <svg className="animate-spin h-8 w-8 text-blue-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
          </svg>
          <div className="text-lg font-semibold text-gray-700 dark:text-gray-200">Updating patient and sleep staging...</div>
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">This may take a few moments.</div>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-red-500 dark:text-red-400">Error loading data.</div>;
  }

  if (!patientInfo || !recordingInfo) {
    return <div className="p-6 text-gray-600 dark:text-gray-300">No data available.</div>;
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900">
      <DemoWalkthrough
        enabled={tourActive}
        steps={recordingInfo?.is_demo ? [
          {
            target: '#demo-report-header',
            title: '👋 Welcome to the Demo',
            content: 'This is a pre-loaded sleep study. Everything here is read-only — explore freely.',
            placement: 'right',
            disableBeacon: true,
          },
          {
            target: '#demo-sleep-parameters',
            title: '📊 Sleep Parameters',
            content: 'Key metrics computed automatically from the EDF recording. Each one has a rule-based estimate, an ML-based estimate, and flags for disagreement or high uncertainty.',
            placement: 'left',
            disableBeacon: true,
          },
          {
            target: '#demo-summary',
            title: '🤖 AI Summary',
            content: 'A narrative report generated automatically from the sleep parameters and staging.',
            placement: 'top',
            disableBeacon: true,
          },
          {
            target: '#demo-hypnogram-link',
            title: '🌙 Hypnogram',
            content: 'The hypnogram shows sleep stage progression across the whole night. Click the 🔍 icon to open the full view — or click Next → to drill into a specific parameter first.',
            placement: 'top',
            disableBeacon: true,
          },
          {
            target: '#demo-n3-magnifying-glass',
            title: '🔬 Deep Dive: N3 Duration',
            content: (
              <>N3 (Deep Sleep) is the most restorative stage. Click the magnifying glass to see exactly how the AI arrived at this value.<DontShowLink /></>
            ),
            placement: 'left',
            disableBeacon: true,
            spotlightClicks: true,
            hideNextButton: true,
            onNext: () => {
              setStep('parameter-detail', recordingId);
              openLevel({
                key: 'parameterDetail',
                label: 'N3 Duration',
                route: `/analysis/${recordingId}/parameter-detail?param=${encodeURIComponent('N3 Duration')}`,
              });
            },
          },
        ] : []}
      />
      <div className="p-6 bg-gray-50 dark:bg-gray-900 rounded-lg w-full lg:max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Patient and Recording Information Box */}
          <div id="demo-report-header" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            {/* Start the tour button — only for demo, only when not yet dismissed */}
            {recordingInfo?.is_demo && !tourActive && !isDismissed() && (
              <div className="px-6 pt-4">
                <button
                  onClick={() => setTourActive(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium transition-colors"
                >
                  ▶ Start the tour
                </button>
              </div>
            )}
            {/* Patient Information */}
            <div className="p-6 border-b border-gray-100 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Patient Information</h2>
              <div className="space-y-3">
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'age', label: 'Age', suffix: 'years' },
                  { key: 'gender', label: 'Gender' },
                  { key: 'height', label: 'Height' },
                  { key: 'weight', label: 'Weight' },
                  { key: 'patient_id', label: 'ID' },
                  { key: 'exhibits_alpha_waves', label: 'Exhibits Alpha Waves', isBoolean: true },
                  { key: 'rem_without_atonia', label: 'REM Without Atonia', isBoolean: true }
                ].map(({ key, label, suffix, isBoolean }) => (
                  <div key={key} className="flex items-center">
                    <div className="w-1/3 text-sm font-medium text-gray-500 dark:text-gray-400">{label}</div>
                    <div className="w-2/3 flex items-center justify-between">
                      {editingField === key ? (
                        <div className="flex-1 mr-2">
                          {isBoolean ? (
                            <button
                              onClick={() => setEditValue(!editValue)}
                              className={`flex items-center space-x-2 px-2 py-1 rounded ${
                                editValue
                                  ? 'text-gray-800 dark:text-gray-200'
                                  : 'text-gray-400 dark:text-gray-500'
                              }`}
                            >
                              <FontAwesomeIcon icon={editValue ? faToggleOn : faToggleOff} size="lg" />
                              <span>{editValue ? 'Yes' : 'No'}</span>
                            </button>
                          ) : (
                            <input
                              type={key === 'age' ? 'number' : 'text'}
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveEdit(key);
                                } else if (e.key === 'Escape') {
                                  setEditingField(null);
                                  setEditValue('');
                                  setEditError(null);
                                }
                              }}
                            />
                          )}
                          {editError && <div className="text-xs text-red-500 mt-1">{editError}</div>}
                        </div>
                      ) : (
                        <div className="text-gray-900 dark:text-gray-100">
                          {isBoolean ? (
                            <div className={`flex items-center space-x-2 ${
                              patientInfo[key]
                                ? 'text-gray-800 dark:text-gray-200'
                                : 'text-gray-400 dark:text-gray-500'
                            }`}>
                              <FontAwesomeIcon icon={patientInfo[key] ? faToggleOn : faToggleOff} size="lg" />
                              <span>{patientInfo[key] ? 'Yes' : 'No'}</span>
                            </div>
                          ) : (
                            <>{patientInfo[key]}{suffix && ` ${suffix}`}</>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => {
                          if (editingField === key) {
                            handleSaveEdit(key);
                          } else {
                            setEditingField(key);
                            setEditValue(isBoolean ? patientInfo[key] : patientInfo[key]);
                            setEditError(null);
                          }
                        }}
                        className="ml-2 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <FontAwesomeIcon icon={editingField === key ? faSave : faPencil} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Recording Information */}
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Recording Information</h2>
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-1/2 text-sm font-medium text-gray-500 dark:text-gray-400">Recording Date</div>
                  <div className="w-1/2 text-gray-900 dark:text-gray-100">{recordingInfo.recording_date}</div>
                </div>
                <div className="flex items-center">
                  <div className="w-1/2 text-sm font-medium text-gray-500 dark:text-gray-400">Signal Quality</div>
                  <div className="w-1/2 text-gray-900 dark:text-gray-100">{recordingInfo.signal_quality}</div>
                </div>
                <div className="flex items-center">
                  <div className="w-1/2 text-sm font-medium text-gray-500 dark:text-gray-400">Total Recording Time</div>
                  <div className="w-1/2 text-gray-900 dark:text-gray-100">{recordingInfo.total_recording_time} min</div>
                </div>
                <div className="flex items-center">
                  <div className="w-1/2 text-sm font-medium text-gray-500 dark:text-gray-400">Lights-Out Time</div>
                  <div className="w-1/2 text-gray-900 dark:text-gray-100">{recordingInfo.lights_out_time}</div>
                </div>
                <div className="flex items-center">
                  <div className="w-1/2 text-sm font-medium text-gray-500 dark:text-gray-400">Lights-On Time</div>
                  <div className="w-1/2 text-gray-900 dark:text-gray-100">{recordingInfo.lights_on_time}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Sleep Parameters Box */}
          <div id="demo-sleep-parameters" className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4">Sleep Parameters</h2>
            <div className="space-y-3">
              {Object.entries(sleepParameters).map(([key, param]) => (
                <div key={key} className="flex items-center">
                  <div className="w-3/6 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {key}
                  </div>
                  <div className="w-1/6 text-gray-900 dark:text-gray-100">
                    {param.value}
                  </div>
                  <div className="w-1/6 flex items-center justify-start space-x-2">
                    {showWarnings && param.disagreementWarning && (
                      <div className="relative inline-block group">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 dark:text-yellow-400 w-4 h-4" />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 dark:bg-black text-white text-sm p-3 rounded-md shadow-lg opacity-0 scale-95 transition-all duration-200 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto z-50">
                          <strong className="block text-yellow-300">Review Needed</strong>
                          <p>{param.disagreementWarningText || 'Disagreement detected between estimation methods.'}</p>
                        </div>
                      </div>
                    )}
                    {showWarnings && param.uncertaintyWarning && (
                      <div className="relative inline-block group">
                        <FontAwesomeIcon icon={faQuestionCircle} className="text-purple-400 dark:text-purple-300 w-4 h-4" />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 dark:bg-black text-white text-sm p-3 rounded-md shadow-lg opacity-0 scale-95 transition-all duration-200 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto z-50">
                          <strong className="block text-purple-300">High Uncertainty</strong>
                          <p>{param.uncertaintyWarningText || 'High uncertainty detected in the pattern-based estimation.'}</p>
                        </div>
                      </div>
                    )}
                    {showWarnings && key === 'AHI' && param.value === '-' && (
                      <div className="relative inline-block group">
                        <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 dark:text-red-500 w-4 h-4" />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 dark:bg-black text-white text-sm p-3 rounded-md shadow-lg opacity-0 scale-95 transition-all duration-200 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto z-50">
                          <strong className="block text-red-400">Error</strong>
                          <p>Missing data.</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="w-1/6 text-right">
                    <button
                      id={key === 'N3 Duration' ? 'demo-n3-magnifying-glass' : undefined}
                      onClick={() => handleOpenParameters(key)}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                      title={`Explore ${key}`}
                    >
                      <FontAwesomeIcon icon={faSearchPlus} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sleep Stages Pie Chart */}
          <div className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Sleep Stages</h2>
            <Plot
              data={sleepStagesData}
              layout={sleepStagesLayout}
              style={{ width: '100%', height: '250px' }}
              useResizeHandler={true}
              key={`pie-${windowSize.width}-${windowSize.height}`}
            />
          </div>

          {/* Summary Section */}
          <div id="demo-summary" className="md:col-span-3 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">Summary</h2>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editedSummary}
                  onChange={(e) => setEditedSummary(e.target.value)}
                  className="w-full h-32 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Enter summary text..."
                />
                {saveError && (
                  <div className="text-red-500 dark:text-red-400 text-sm">
                    Error saving summary: {saveError}
                  </div>
                )}
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveSummary}
                    disabled={isSaving}
                    className="px-3 py-1 text-sm bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-gray-700 dark:text-gray-300">
                  {summary?.summary_text || (
                    <span className="text-gray-500 dark:text-gray-400 italic">No summary available. Click the edit button to create one.</span>
                  )}
                </p>
                <div className="mt-2 flex justify-between items-center">
                  <div className="text-sm text-gray-400 dark:text-gray-500">
                    {summary?.is_automatically_generated ? 'Automatically generated' : 'Manually edited'}
                  </div>
                  <button 
                    onClick={handleEditSummary}
                    className="cursor-pointer text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
                    title="Edit summary"
                  >
                    <FontAwesomeIcon icon={faPencil} />
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Hypnogram Section */}
          <div id="demo-hypnogram-link" className="md:col-span-3 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Hypnogram</h2>
              <div className="flex items-center space-x-4">
                {showWarnings && (
                  <div className="relative inline-block group">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500 dark:text-yellow-400" />
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-900 dark:bg-black text-white text-sm p-3 rounded-md shadow-lg opacity-0 scale-95 transition-all duration-200 pointer-events-none group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto z-50">
                      <strong className="block text-yellow-300">Review Needed</strong>
                      <p>Multiple areas of disagreement between direct hypnogram estimation and epoch-by-epoch classification.</p>
                      <p>Multiple discrepancies between rule-based scoring and machine learning-based scoring.</p>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => openLevel({ 
                    key: 'hypnogram', 
                    label: 'Hypnogram', 
                    route: `/analysis/hypnogram` 
                  })}
                  className="cursor-pointer text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 z-10"
                  title="Explore..."
                >
                  <FontAwesomeIcon icon={faSearchPlus} />
                </button>
              </div>
            </div>
            <div className="relative">
              <Plot
                data={hypnogramData}
                layout={hypnogramLayout}
                style={{ width: '100%' }}
                config={{ displayModeBar: false }}
                useResizeHandler={true}
                key={`hypno-${windowSize.width}-${windowSize.height}`}
              />
            </div>
          </div>
        </div>
        
        {/* Add this after the last grid item */}
        <div className="mt-8 flex justify-center space-x-4">
          <button
            onClick={generatePDF}
            disabled={generatingPdf}
            className="flex items-center space-x-2 px-4 py-2 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors duration-200 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faFilePdf} className="text-blue-600" />
            <span>{generatingPdf ? 'Generating PDF...' : 'Export as PDF'}</span>
          </button>
          <button
            onClick={() => {
              // Create CSV content
              const csvContent = ['Timestamp,Sleep Stage'];
              epochs.forEach(epoch => {
                csvContent.push(`${epoch.epoch_start},${epoch.sleep_stage}`);
              });
              
              // Create and trigger download
              const blob = new Blob([csvContent.join('\n')], { type: 'text/csv;charset=utf-8;' });
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `hypnogram_${patientInfo.patient_id}_${recordingInfo.recording_date}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            }}
            className="flex items-center space-x-2 px-4 py-2 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded transition-colors duration-200"
          >
            <FontAwesomeIcon icon={faFileCsv} className="text-green-600" />
            <span>Export Hypnogram as CSV</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Report;
